import { Avatar, Tooltip, mergeClasses } from "@fluentui/react-components";
import { Eye20Regular } from "@fluentui/react-icons";
import { getFeedback } from "api";
import { QueryCacheConfig } from "common/constants";
import { AdminListPage } from "components/adminList/AdminListPage";
import { AdminRowAction } from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { findSidebarItem } from "components/adminSidebar/adminSidebarConfig";
import { useAdminListFilters } from "hooks/useAdminListFilters";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { ChevronIcon } from "icons/ChevronIcon";
import { ExportIcon } from "icons/ExportIcon";
import { SearchIcon } from "icons/SearchIcon";
import { IFeedback } from "models/IFeedback";
import { ITableColumn } from "models/ITableColumn";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedAdminContentHeaderStyles,
  useSharedButtonStyles,
  useSharedTagStyles,
} from "theme/sharedStyles";
import { formatDate } from "utils/generalUtils";
import { ExportToExcelDialog } from "./exportToExcelDialog/ExportToExcelDialog";
import { FeedbackViewModal } from "./feedbackViewModal/FeedbackViewModal";
import { useManageFeedbackStyles } from "./ManageFeedback.styles";

export const ManageFeedback = (): JSX.Element => {
  const cellStyles = useManageFeedbackStyles();
  const colStyles = useAdminColumnStyles();
  const headerClasses = useSharedAdminContentHeaderStyles();
  const buttonStyles = useSharedButtonStyles();
  const tagStyles = useSharedTagStyles();
  const user: any = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const isPlatformAdmin = user?.roles.includes("platformAdmin");
  const [selectedFeedback, setSelectedFeedback] = useState<IFeedback | null>(
    null,
  );
  const [showExportToExcelDialog, setShowToExportDialog] =
    useState<boolean>(false);
  const { filters, setFilter } = useAdminListFilters({
    cacheKey: "Feedback",
    filterKeys: ["feedbackType", "requestAssistance"] as const,
  });

  const { data: feedbackList = [], isLoading } = useQuery(
    "feedback",
    getFeedback,
    {
      refetchOnWindowFocus: false,
      staleTime: QueryCacheConfig.STALE_TIME_DEFAULT,
    },
  );

  const columns: ITableColumn<IFeedback>[] = useMemo(
    () => [
      {
        columnId: "content",
        displayValue: "User Feedback",
        className: colStyles.wide,
        renderHeaderCell: () => "User Feedback",
        renderCell: (item: IFeedback) => (
          <Tooltip
            content={item.content}
            relationship="description"
            positioning="above"
          >
            <span className={cellStyles.feedbackText} title={item.content}>
              {item.content}
            </span>
          </Tooltip>
        ),
        compare: (a, b) => a.content.localeCompare(b.content),
      },
      {
        columnId: "feedbackType",
        displayValue: "Feedback Type",
        className: colStyles.normal,
        renderHeaderCell: () => "Feedback Type",
        renderCell: (feedback: IFeedback) => (
          <span
            className={mergeClasses(
              tagStyles.tag,
              feedback.feedbackType === "like"
                ? tagStyles.tagSuccess
                : tagStyles.tagError,
            )}
          >
            {feedback.feedbackType === "like" ? "Positive" : "Negative"}
          </span>
        ),
        compare: (a, b) => a.feedbackType.localeCompare(b.feedbackType),
      },
      {
        columnId: "requestAssistance",
        displayValue: "Request Assistance",
        className: colStyles.normal,
        renderHeaderCell: () => "Request Assistance",
        renderCell: (feedback: IFeedback) =>
          feedback.requestAssistance === true ? "Yes" : "No",
        compare: (a, b) =>
          Number(a.requestAssistance) - Number(b.requestAssistance),
      },
      {
        columnId: "displayName",
        displayValue: "Author",
        className: colStyles.normal,
        renderHeaderCell: () => "Author",
        renderCell: (feedback: IFeedback): JSX.Element => {
          if (!feedback || !feedback.user) {
            return <Avatar />;
          }

          return (
            <Tooltip
              content={feedback.user.displayName}
              relationship="description"
              positioning="above"
            >
              <div className={cellStyles.authorCell}>
                <Avatar
                  aria-label={feedback.user.displayName}
                  name={feedback.user.displayName}
                  size={32}
                />
                <span className={cellStyles.authorName}>
                  {feedback.user.displayName}
                </span>
              </div>
            </Tooltip>
          );
        },
        compare: (a: IFeedback, b: IFeedback): number =>
          a.user.displayName.localeCompare(b.user.displayName),
      },
      {
        columnId: "submittedAt",
        displayValue: "Submitted At",
        className: colStyles.normal,
        renderHeaderCell: () => "Submitted At",
        renderCell: (feedback: IFeedback) => formatDate(feedback.submittedAt),
        compare: (a, b) => a.submittedAt - b.submittedAt,
      },
    ],
    [
      cellStyles.feedbackText,
      cellStyles.authorCell,
      cellStyles.authorName,
      colStyles.normal,
      colStyles.wide,
      tagStyles.tag,
      tagStyles.tagSuccess,
      tagStyles.tagError,
    ],
  );

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<IFeedback>({
    cacheKey: "Feedback",
    defaultSortColumn: "submittedAt",
    defaultDescending: true,
  });

  const searchFields = useMemo(
    () => ["content", "feedbackType", "user"] as (keyof IFeedback)[],
    [],
  );

  const {
    searchQuery: internalSearchQuery,
    handleSearchChange,
    handleSearchValueChange,
    filteredItems,
  } = useAdminListSearch<IFeedback>({
    items: feedbackList,
    searchFields,
  });

  const feedbackTypeOptions = useMemo(
    () => [
      { key: "like", text: "Positive" },
      { key: "dislike", text: "Negative" },
    ],
    [],
  );

  const requestAssistanceOptions = useMemo(
    () => [
      { key: "yes", text: "Yes" },
      { key: "no", text: "No" },
    ],
    [],
  );

  const fullyFilteredItems = useMemo(() => {
    let result = filteredItems;
    if (filters.feedbackType) {
      result = result.filter((fb) => fb.feedbackType === filters.feedbackType);
    }
    if (filters.requestAssistance) {
      result = result.filter((fb) =>
        filters.requestAssistance === "yes"
          ? fb.requestAssistance === true
          : fb.requestAssistance !== true,
      );
    }
    return result;
  }, [filteredItems, filters.feedbackType, filters.requestAssistance]);

  const sortedItems = useMemo(
    () => sortItems(fullyFilteredItems, columns),
    [fullyFilteredItems, sortItems, columns],
  );

  const emptyStateMessage = useMemo(() => {
    const hasActiveFilters =
      internalSearchQuery || filters.feedbackType || filters.requestAssistance;
    return hasActiveFilters
      ? "No feedback matches the current filters."
      : "No feedback found.";
  }, [internalSearchQuery, filters.feedbackType, filters.requestAssistance]);

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination<IFeedback>({
    items: sortedItems,
    itemsPerPage: 10,
  });

  const handleViewFeedback = (feedback: IFeedback) => {
    setSelectedFeedback(feedback);
  };

  const handleExportToExcel = (): void => {
    setShowToExportDialog(true);
  };

  const rowActions: AdminRowAction<IFeedback>[] = useMemo(
    () => [
      {
        icon: <Eye20Regular />,
        tooltip: "View",
        onClick: handleViewFeedback,
      },
    ],
    [],
  );

  const sidebarItem = useMemo(() => findSidebarItem("feedback"), []);

  return (
    <>
      <div className={headerClasses.root}>
        <div
          className={mergeClasses(
            headerClasses.titleRow,
            headerClasses.titleRowWithFilters,
          )}
        >
          <h1 className={headerClasses.title}>
            {sidebarItem?.label || "User Feedback"}
          </h1>
          {isPlatformAdmin && (
            <div className={headerClasses.actions}>
              <button
                className={mergeClasses(
                  headerClasses.actionButton,
                  buttonStyles.secondaryButton,
                )}
                onClick={handleExportToExcel}
                type="button"
              >
                <ExportIcon /> Export
              </button>
            </div>
          )}
        </div>
        <div className={headerClasses.filterRow}>
          <div className={headerClasses.searchInputWrapper}>
            <SearchIcon className={headerClasses.searchIcon} />
            <input
              type="text"
              className={headerClasses.searchInput}
              placeholder="Search feedback..."
              value={internalSearchQuery}
              onChange={(e) => handleSearchValueChange(e.target.value)}
              aria-label="Search feedback..."
            />
          </div>
          <div className={headerClasses.filterSelectWrapper}>
            <select
              className={headerClasses.filterSelect}
              value={filters.feedbackType}
              onChange={(e) => setFilter("feedbackType", e.target.value)}
              aria-label="All Feedback Types"
            >
              <option value="">All Feedback Types</option>
              {feedbackTypeOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.text}
                </option>
              ))}
            </select>
            <ChevronIcon className={headerClasses.filterChevron} />
          </div>
          <div className={headerClasses.filterSelectWrapper}>
            <select
              className={headerClasses.filterSelect}
              value={filters.requestAssistance}
              onChange={(e) => setFilter("requestAssistance", e.target.value)}
              aria-label="Request Assistance"
            >
              <option value="">Request Assistance</option>
              {requestAssistanceOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.text}
                </option>
              ))}
            </select>
            <ChevronIcon className={headerClasses.filterChevron} />
          </div>
        </div>
      </div>
      <AdminListPage<IFeedback>
        title="Feedback"
        items={feedbackList}
        columns={columns}
        isLoading={isLoading}
        headerActions={[]}
        searchPlaceholder="Search feedback..."
        searchQuery={internalSearchQuery}
        onSearchChange={handleSearchChange}
        hideHeader={true}
        rowActions={rowActions}
        getRowId={(feedback) => feedback.id || ""}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        emptyStateMessage={emptyStateMessage}
      />

      {showExportToExcelDialog && (
        <ExportToExcelDialog
          isOpen={showExportToExcelDialog}
          onDismiss={() => setShowToExportDialog(false)}
          feedback={feedbackList}
        />
      )}

      {selectedFeedback && (
        <FeedbackViewModal
          feedback={selectedFeedback}
          isOpen={!!selectedFeedback}
          onDismiss={() => setSelectedFeedback(null)}
        />
      )}
    </>
  );
};
